import cn from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Tooltip, TooltipInteractionEnum } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import {
  type AttributionEntryMode,
  type ImportMode,
  usePOIImporter
} from '@/components/common/POIImporter/POIImporter.context';
import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { getCommittedAttributionType } from '../../MultiFlightMap.helpers';
import { ToolGroup } from './ToolGroup/ToolGroup';

import type { MapMode } from '../MapView/map.types';
import type { ToolGroupConfig } from './ActionToolsPanel.types';

import './actionToolsPanel.scss';

import { useModals } from '@/router';

export function ActionToolsPanel() {
  const { setAttributionMode, setImportMode, setShowModal: setShowAttributionPOIModal } = usePOIImporter();
  const modals = useModals();

  const handleShowAttributionPOI = (mode: ImportMode, attributionMode: AttributionEntryMode = 'attributionPOI') => {
    setAttributionMode(attributionMode);
    setImportMode(mode);
    setShowAttributionPOIModal(true);
  };

  const { formatMessage } = useIntl();
  const {
    entityType,
    isSaving,
    isLoading,
    loadingSourcesList,
    mode,
    processBatchUploadFileStatus,
    setMode,
    temporaryVenues,
    type
  } = useMultiFlightMap();

  const hideSubGroupDescriptions =
    (entityType === 'insertionOrder' || entityType === 'lineItem') && type === CampaignFamilyEnum.dooh;
  const [isExpanded, setIsExpanded] = useState(true);

  const handleShowCartographyModal = () => {
    modals.open('/campaigns/new/cartographyModal', {
      state: { modalId: 'cartography-modal' }
    });
  };

  const handleShowEntityCSVUploadModal = () => {
    modals.open('/campaigns/new/uploadLocationsModal', {
      state: { modalId: 'upload-file-modal' }
    });
  };

  const handleShowPoiBrandsModal = (poiBrandsModalType?: 'screens' | 'venues') => {
    if (poiBrandsModalType === 'venues') {
      modals.open('/campaigns/new/poiFinderVenuesModal', {
        state: { modalId: 'poi-finder-venues-modal' }
      });
    } else {
      modals.open('/campaigns/new/poiFinderScreensModal', {
        state: { modalId: 'poi-finder-screens-modal' }
      });
    }
  };

  const handleModeChange = (id: MapMode) => {
    setMode(id);
  };

  const exploreAudienceTool = {
    'data-testid': 'tool-button-actionToolsPanel-exploreAudience',
    icon:
      isSaving === true || isLoading === true
        ? 'fa-duotone fa-solid fa-spinner fa-spin'
        : 'fa-duotone fa-solid fa-users',
    id: 'actionToolsPanel-exploreAudience',
    isActive: mode === 'audience_view_tool',
    onClick: () => handleModeChange('audience_view_tool'),
    text: formatMessage({
      defaultMessage: 'Explore your Audience',
      id: 'actionToolsPanel.plan.exploreAudience'
    }),
    tooltip: formatMessage({
      defaultMessage: "Discover your audience using Sonata Location Intelligence's tool (Sonata LI).",
      id: 'actionToolsPanel.tooltip.exploreAudience'
    })
  };

  const planTools = [exploreAudienceTool];

  const activateTools =
    type === CampaignFamilyEnum.dooh
      ? [
          {
            icon: 'fa-kit fa-screen',
            id: 'actionToolsPanel-exploreAllScreens',
            onClick: () => handleShowPoiBrandsModal('screens'),
            text: formatMessage({
              defaultMessage: 'Explore all Screens',
              id: 'actionToolsPanel.plan.exploreAllScreens'
            }),
            tooltip: formatMessage({
              defaultMessage: 'Browse our extensive library of available screens or use your own.',
              id: 'actionToolsPanel.tooltip.exploreAllScreens'
            })
          }
        ]
      : [
          {
            'data-testid': 'tool-button-actionToolsPanel-cartography',
            icon: 'fa-kit fa-map-no-background',
            id: 'actionToolsPanel-cartography',
            onClick: () => handleShowCartographyModal(),
            text: formatMessage({
              defaultMessage: 'Cartography selection',
              id: 'actionToolsPanel.activate.cartography'
            }),
            tooltip: formatMessage({
              defaultMessage: 'Add multiple predefined areas such as countries, Provinces, Postal codes, Geohashes...',
              id: 'actionToolsPanel.tooltip.cartography'
            })
          },
          {
            'data-testid': 'tool-button-actionToolsPanel-addressRadius',
            icon: 'fa-kit fa-csv-file',
            id: 'actionToolsPanel-addressRadius',
            loading: processBatchUploadFileStatus === 'processing',
            onClick: () => handleShowEntityCSVUploadModal(),
            text: formatMessage({
              defaultMessage: 'Address & Radius file',
              id: 'actionToolsPanel.activate.addressRadius'
            }),
            tooltip: formatMessage({
              defaultMessage:
                'Upload your own addresses from a file and add a radius around them to set them as areas.',
              id: 'actionToolsPanel.tooltip.addressRadius'
            })
          },
          {
            'data-testid': 'tool-button-actionToolsPanel-areasFromPois',
            icon: 'fa-kit fa-areas-from-pois-no-background',
            id: 'actionToolsPanel-areasFromPois',
            onClick: () => handleShowPoiBrandsModal('venues'),
            text: formatMessage({ defaultMessage: 'Areas from POIs', id: 'actionToolsPanel.activate.areasFromPois' }),
            tooltip: formatMessage({
              defaultMessage: 'Convert multiple POIs of Interest into Activation Areas with a custom radius.',
              id: 'actionToolsPanel.tooltip.areasFromPois'
            })
          },
          {
            'data-testid': 'tool-button-actionToolsPanel-polygonArea',
            icon: 'fa-kit fa-polygon-area-no-background',
            id: 'actionToolsPanel-polygonArea',
            isActive: mode === 'draw-polygons',
            onClick: () => handleModeChange('draw-polygons'),
            text: formatMessage({ defaultMessage: 'Polygon Area', id: 'actionToolsPanel.activate.polygonArea' }),
            tooltip: formatMessage({
              defaultMessage: 'Draw a custom shape directly on the map to define a specific area of interest.',
              id: 'actionToolsPanel.tooltip.polygonArea'
            })
          },
          {
            'data-testid': 'tool-button-actionToolsPanel-circularArea',
            icon: 'fa-kit fa-circle-dashed-no-background',
            id: 'actionToolsPanel-circularArea',
            isActive: mode === 'add-circles',
            onClick: () => handleModeChange('add-circles'),
            text: formatMessage({ defaultMessage: 'Circular Area', id: 'actionToolsPanel.activate.circularArea' }),
            tooltip: formatMessage({
              defaultMessage: 'Suited for defining a circular perimeter by selecting a central point on the map.',
              id: 'actionToolsPanel.tooltip.circularArea'
            })
          }
        ];

  // Cross-disable the two sibling flows: a campaign can only carry one kind of
  // venue-based attribution at a time. The reliable signal is `geoDataId` on
  // the actual live venues (see getCommittedAttributionType), not
  // `attributionSettings`, which can be `enabled` on legacy entities with no
  // Proximity Points venues at all.
  const committedAttributionType = getCommittedAttributionType(temporaryVenues);
  const hasDriveToStoreAttribution = committedAttributionType === 'driveToStore';
  const hasFootfallOnlyAttribution = committedAttributionType === 'footfall';
  const [attributionTab, setAttributionTab] = useState(hasFootfallOnlyAttribution ? 'proximity' : 'store');

  const disabledAttributionTooltip = formatMessage({
    defaultMessage:
      'This option is incompatible with your attribution method. Remove the elements from your list to enable it.',
    id: 'actionToolsPanel.measureAttribution.disabledTooltip'
  });

  const storeVisitsTools = [
    {
      'data-testid': 'tool-button-actionToolsPanel-poiInventory',
      disabled: hasFootfallOnlyAttribution,
      icon: 'fa-duotone fa-solid fa-store',
      id: 'actionToolsPanel-poiInventory',
      loading: loadingSourcesList,
      onClick: () => handleShowAttributionPOI('inventory', 'attributionPOI'),
      text: formatMessage({
        defaultMessage: 'POI Inventory',
        id: 'actionToolsPanel.measureAttribution.poiInventory'
      }),
      tooltip: formatMessage({
        defaultMessage:
          'You can integrate pre-existing Points of Interest compiled from various public and commercial providers, to measure their attribution.',
        id: 'actionToolsPanel.tooltip.poiInventory'
      })
    },
    {
      'data-testid': 'tool-button-actionToolsPanel-uploadPois',
      disabled: hasFootfallOnlyAttribution,
      icon: 'fa-kit fa-upload-pois',
      id: 'actionToolsPanel-uploadPois',
      onClick: () => handleShowAttributionPOI('upload', 'attributionPOI'),
      text: formatMessage({
        defaultMessage: 'Upload your POIs',
        id: 'actionToolsPanel.measureAttribution.uploadPois'
      }),
      tooltip: formatMessage({
        defaultMessage:
          'You can integrate pre-existing Points of Interest compiled from various public and commercial providers, to measure their attribution.',
        id: 'actionToolsPanel.tooltip.uploadPois'
      })
    }
  ];

  const proximityVisitsTools = [
    {
      'data-testid': 'tool-button-actionToolsPanel-pointsInventory',
      disabled: hasDriveToStoreAttribution,
      icon: 'fa-solid fa-thumbtack',
      id: 'actionToolsPanel-pointsInventory',
      onClick: () => handleShowAttributionPOI('inventory', 'proximityPoints'),
      text: formatMessage({
        defaultMessage: 'Coordinates Inventory',
        id: 'actionToolsPanel.measureAttribution.pointsInventory'
      }),
      tooltip: formatMessage({
        defaultMessage:
          'You can add custom lat/lng points — including ones that may not exist on Google Maps — to measure their footfall attribution.',
        id: 'actionToolsPanel.tooltip.pointsInventory'
      })
    },
    {
      'data-testid': 'tool-button-actionToolsPanel-uploadPoints',
      disabled: hasDriveToStoreAttribution,
      icon: 'fa-kit fa-solid-thumbtack-upload',
      id: 'actionToolsPanel-uploadPoints',
      onClick: () => handleShowAttributionPOI('upload', 'proximityPoints'),
      text: formatMessage({
        defaultMessage: 'Upload your Coordinates',
        id: 'actionToolsPanel.measureAttribution.uploadPoints'
      }),
      tooltip: formatMessage({
        defaultMessage:
          'You can add custom lat/lng points — including ones that may not exist on Google Maps — to measure their footfall attribution.',
        id: 'actionToolsPanel.tooltip.uploadPoints'
      })
    }
  ];

  const groups: ToolGroupConfig[] = [
    {
      icon: 'fa-kit fa-solid-fa-audience-centric',
      infoTooltip:
        type === CampaignFamilyEnum.dooh
          ? formatMessage({
              defaultMessage:
                "Discover all screens available in our Sonata Location Intelligence's tool (Sonata LI) platform or upload your own.",
              id: 'actionToolsPanel.plan.doohGroupInfo'
            })
          : formatMessage({
              defaultMessage:
                "Discover your audience and export your areas to our Sonata Location Intelligence's tool (Sonata LI) platform.",
              id: 'actionToolsPanel.plan.groupInfo'
            }),
      label: formatMessage({ defaultMessage: 'Plan', id: 'actionToolsPanel.plan.group' }),
      subGroups: [
        {
          description: hideSubGroupDescriptions
            ? undefined
            : formatMessage({
                defaultMessage: 'by using Sonata LI',
                id: 'actionToolsPanel.plan.group.description'
              }),
          tools: planTools
        }
      ]
    },
    {
      icon: 'fa-duotone fa-solid fa-map',
      infoTooltip: formatMessage({
        defaultMessage:
          'Define Activation Areas for your campaign. In some cases, you can both activate and add Attribution.',
        id: 'actionToolsPanel.activate.groupInfo'
      }),
      label: formatMessage({ defaultMessage: 'Activate', id: 'actionToolsPanel.activate.group' }),
      subGroups: [
        {
          description: hideSubGroupDescriptions
            ? undefined
            : formatMessage({
                defaultMessage: 'Set up Activation Areas by drawing shapes, uploading a file, or via POIs.',
                id: 'actionToolsPanel.activate.group.description'
              }),
          tools: activateTools
        }
      ]
    },
    {
      activeTab: attributionTab,
      icon: 'fa-solid fa-chart-mixed',
      infoTooltip: formatMessage({
        defaultMessage:
          'Set up Attribution for POIs to measure them. You will also be able to both set attribution and add as Activation Area.',
        id: 'actionToolsPanel.measureAttribution.groupInfo'
      }),
      label:
        type === CampaignFamilyEnum.dooh
          ? formatMessage({
              defaultMessage: 'Measure Attribution by POIs or Proximity Coordinates',
              id: 'actionToolsPanel.measureAttribution.group.dooh'
            })
          : formatMessage({
              defaultMessage: 'Measure Attribution by...',
              id: 'actionToolsPanel.measureAttribution.group'
            }),
      onChangeTab: setAttributionTab,
      subGroups: [
        {
          description: hideSubGroupDescriptions
            ? undefined
            : formatMessage({
                defaultMessage: 'Store Visits (POI-based)',
                id: 'actionToolsPanel.measureAttribution.storeVisits.description'
              }),
          disabledTooltip: hasFootfallOnlyAttribution ? disabledAttributionTooltip : undefined,
          tab: {
            id: 'store',
            title: formatMessage({
              defaultMessage: 'Store',
              id: 'actionToolsPanel.measureAttribution.tab.store'
            }),
            tooltip: formatMessage({
              defaultMessage: 'Based on POIs',
              id: 'actionToolsPanel.measureAttribution.tab.store.tooltip'
            })
          },
          tools: storeVisitsTools
        },
        {
          description: hideSubGroupDescriptions
            ? undefined
            : formatMessage({
                defaultMessage: 'Proximity Visits (Radio-based)',
                id: 'actionToolsPanel.measureAttribution.proximityVisits.description'
              }),
          disabledTooltip: hasDriveToStoreAttribution ? disabledAttributionTooltip : undefined,
          tab: {
            id: 'proximity',
            title: formatMessage({
              defaultMessage: 'Proximity',
              id: 'actionToolsPanel.measureAttribution.tab.proximity'
            }),
            tooltip: formatMessage({
              defaultMessage: 'Based on coordinates',
              id: 'actionToolsPanel.measureAttribution.tab.proximity.tooltip'
            })
          },
          tools: proximityVisitsTools
        }
      ]
    }
  ];

  const getVisibleGroups = (groupList: ToolGroupConfig[]): ToolGroupConfig[] =>
    groupList
      .map(group => ({
        ...group,
        subGroups: group.subGroups
          .map(subGroup => ({ ...subGroup, tools: subGroup.tools.filter(tool => tool.visible !== false) }))
          .filter(subGroup => subGroup.tools.length > 0)
      }))
      .filter(group => group.subGroups.length > 0);

  return (
    <div
      className={cn('actionToolsPanel', { 'actionToolsPanel--isCollapsed': !isExpanded })}
      data-testid="action-tools-panel"
    >
      {isExpanded && (
        <div className="actionToolsPanel__header">
          <span className="actionToolsPanel__title">
            {formatMessage({ defaultMessage: 'Action tools', id: 'actionToolsPanel.header' })}
          </span>
          <ButtonUI
            className="actionToolsPanel__toggleButton"
            icon="fa-solid fa-chevron-up"
            id="action-tools-panel-toggle"
            onClick={() => setIsExpanded(prev => !prev)}
            shape="link"
            size="ultraSmall"
            title={formatMessage({ defaultMessage: 'Collapse', id: 'actionToolsPanel.toggle.collapse' })}
            type="tertiary"
          />
        </div>
      )}
      {isExpanded && (
        <div className="actionToolsPanel__body" data-testid="action-tools-panel-body">
          {getVisibleGroups(groups).map(group => (
            <ToolGroup
              data-testid={`tool-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
              key={group.label}
              {...group}
            />
          ))}
        </div>
      )}
      {!isExpanded && (
        <div className="actionToolsPanel__collapsedRow" data-testid="action-tools-panel-collapsed">
          <Tooltip
            config={{
              arrow: true,
              component: (
                <div>
                  {formatMessage({
                    defaultMessage: 'Click to expand all options.',
                    id: 'actionToolsPanel.toggle.expandTooltip'
                  })}
                </div>
              ),
              forcePlacement: true,
              interactions: [TooltipInteractionEnum.hover],
              placement: 'top',
              portal: true,
              strategy: 'absolute'
            }}
          >
            <ButtonUI
              className="actionToolsPanel__toggleButton"
              icon="fa-solid fa-grid-2"
              id="action-tools-panel-toggle"
              onClick={() => setIsExpanded(prev => !prev)}
              size="small"
              title={formatMessage({ defaultMessage: 'Expand', id: 'actionToolsPanel.toggle.expand' })}
              type="primary"
            />
          </Tooltip>
          {getVisibleGroups(groups).map(group => (
            <div className="actionToolsPanel__collapsedItem" key={group.label}>
              <Tooltip
                config={{
                  component: <ToolGroup isCollapsed {...group} />,
                  customBorderColor: 'var(--color-sonata-secondary-white)',
                  forcePlacement: true,
                  interactions: [TooltipInteractionEnum.hover],
                  offset: 16,
                  placement: 'top',
                  portal: true,
                  strategy: 'absolute'
                }}
              >
                <ButtonUI
                  icon={group.icon}
                  id={`action-tools-panel-collapsed-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                  size="ultraSmall"
                  text={group.label}
                  type="tertiary"
                />
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
